import { message } from '@surecompanies/core_components'
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationOptions,
  UseQueryOptions,
} from '@tanstack/react-query'
import epsClient from 'enrollment-platform/api/eps.client'

export enum CensusIngestionChannel {
  AMA = 'AMA',
  API = 'API',
  MANUAL_UPLOAD = 'MANUAL_UPLOAD',
}

export interface CensusFieldCatalogItem {
  field: string
  isEligibilityImpacting: boolean
}

export interface CompanyIgnoreRuleVersion {
  id: string
  clientPublicKey: string
  version: number
  ignoredFields: string[]
  channels: CensusIngestionChannel[]
  isActive: boolean
  activatedAt: string | null
  deactivatedAt?: string | null
  changeReason?: string | null
  createdBy?: string | null
  createdAt: string
}

export interface SaveCompanyIgnoreRulesRequest {
  ignoredFields: string[]
  channels: CensusIngestionChannel[]
  changeReason?: string
  allowEligibilityImpactingFields?: boolean
}

export const COMPANY_IGNORE_RULES_QUERY_KEY = 'company-ignore-rules'
export const CENSUS_FIELD_CATALOG_QUERY_KEY = 'census-field-catalog'

function companyIgnoreRulesPath(clientPublicKey: string) {
  return `/api/v1/census-customer-ignored/${clientPublicKey}/ignore-rules`
}

export async function getCensusFieldCatalog(
  clientPublicKey: string
): Promise<CensusFieldCatalogItem[]> {
  const response = await epsClient.get<CensusFieldCatalogItem[]>(
    `${companyIgnoreRulesPath(clientPublicKey)}/available-fields`
  )
  return response.data
}

export async function getActiveCompanyIgnoreRules(
  clientPublicKey: string
): Promise<CompanyIgnoreRuleVersion | null> {
  try {
    const response = await epsClient.get<CompanyIgnoreRuleVersion>(
      `${companyIgnoreRulesPath(clientPublicKey)}/active`
    )
    return response.data
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null
    }
    throw error
  }
}

export async function getCompanyIgnoreRuleHistory(
  clientPublicKey: string
): Promise<CompanyIgnoreRuleVersion[]> {
  const response = await epsClient.get<CompanyIgnoreRuleVersion[]>(
    companyIgnoreRulesPath(clientPublicKey)
  )
  return response.data
}

export async function saveCompanyIgnoreRules({
  clientPublicKey,
  body,
}: {
  clientPublicKey: string
  body: SaveCompanyIgnoreRulesRequest
}): Promise<CompanyIgnoreRuleVersion> {
  const response = await epsClient.post<CompanyIgnoreRuleVersion>(
    companyIgnoreRulesPath(clientPublicKey),
    body
  )
  return response.data
}

export function useCensusFieldCatalogQuery(
  clientPublicKey: string,
  options?: UseQueryOptions<
    CensusFieldCatalogItem[],
    Error,
    CensusFieldCatalogItem[]
  >
) {
  return useQuery<CensusFieldCatalogItem[], Error, CensusFieldCatalogItem[]>({
    queryKey: [CENSUS_FIELD_CATALOG_QUERY_KEY, clientPublicKey],
    queryFn: () => getCensusFieldCatalog(clientPublicKey),
    enabled: !!clientPublicKey,
    // Fetch once per page visit — no silent background refetches.
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  })
}

export function useActiveCompanyIgnoreRulesQuery(
  clientPublicKey: string,
  options?: UseQueryOptions<
    CompanyIgnoreRuleVersion | null,
    Error,
    CompanyIgnoreRuleVersion | null
  >
) {
  return useQuery<
    CompanyIgnoreRuleVersion | null,
    Error,
    CompanyIgnoreRuleVersion | null
  >({
    queryKey: [COMPANY_IGNORE_RULES_QUERY_KEY, clientPublicKey, 'active'],
    queryFn: () => getActiveCompanyIgnoreRules(clientPublicKey),
    enabled: !!clientPublicKey,
    // Fetch once per page visit — no silent background refetches. A
    // successful save explicitly invalidates this query to refresh it.
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  })
}

export function useCompanyIgnoreRuleHistoryQuery(
  clientPublicKey: string,
  options?: UseQueryOptions<
    CompanyIgnoreRuleVersion[],
    Error,
    CompanyIgnoreRuleVersion[]
  >
) {
  return useQuery<
    CompanyIgnoreRuleVersion[],
    Error,
    CompanyIgnoreRuleVersion[]
  >({
    queryKey: [COMPANY_IGNORE_RULES_QUERY_KEY, clientPublicKey, 'history'],
    queryFn: () => getCompanyIgnoreRuleHistory(clientPublicKey),
    enabled: !!clientPublicKey,
    // Fetch once per page visit — no silent background refetches. A
    // successful save explicitly invalidates this query to refresh it.
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  })
}

export function useSaveCompanyIgnoreRulesMutation(
  options?: UseMutationOptions<
    CompanyIgnoreRuleVersion,
    Error,
    { clientPublicKey: string; body: SaveCompanyIgnoreRulesRequest }
  >
) {
  const queryClient = useQueryClient()

  return useMutation<
    CompanyIgnoreRuleVersion,
    Error,
    { clientPublicKey: string; body: SaveCompanyIgnoreRulesRequest }
  >({
    ...options,
    mutationFn: saveCompanyIgnoreRules,
    onSuccess: async (data, variables, context) => {
      // Re-query the active config and version history — this is the only
      // refetch that happens for this feature (queries otherwise fetch once
      // per page visit). Catalog fields are untouched since a save can't
      // change them.
      await queryClient.invalidateQueries({
        queryKey: [COMPANY_IGNORE_RULES_QUERY_KEY, variables.clientPublicKey],
      })

      // invalidateQueries swallows refetch failures by default, so check the
      // resulting query state directly — otherwise a failed post-save
      // refetch would leave stale data on screen with no indication.
      const activeFailed =
        queryClient.getQueryState([
          COMPANY_IGNORE_RULES_QUERY_KEY,
          variables.clientPublicKey,
          'active',
        ])?.status === 'error'
      const historyFailed =
        queryClient.getQueryState([
          COMPANY_IGNORE_RULES_QUERY_KEY,
          variables.clientPublicKey,
          'history',
        ])?.status === 'error'

      if (activeFailed || historyFailed) {
        message.warning(
          'Saved successfully, but refreshing the latest data failed. Reload the page to see the up-to-date active config and version history.'
        )
      }

      options?.onSuccess?.(data, variables, context)
    },
  })
}
